// ABOUTME: One application in full: the card and its step tracker, the interview card when a round exists, the PJL
// ABOUTME: notices and what the talent applied with, and the withdrawal while the backend says it is still possible;
// ABOUTME: with PJL, all of it as sections of the page of the job applied to, in that page's style, with the
// ABOUTME: withdrawal in the page's footer where "Bewerben" sits on a listing, when the page hands a job header in.
import { ReactNode, useState } from 'react';
import Box from '@mui/material/Box';
import { AppButton } from 'components/AppButton';
import { NotFoundErrorScreen } from 'components/screens';
import { Text } from 'components/Text';
import { sendAtsApplicationWithdrawn } from 'modules/analytics';
import { getTrans, TranslationKey } from 'modules/i18n';
import { TalentInterviewCard } from 'modules/interviews';
import { InterviewStatus } from 'modules/interviews/types';
import {
  JobPageFooter,
  JobPageSection
} from 'modules/jobListings/components/JobPageParts';
import {
  APPLICATION_NOT_FOUND_ERROR_CODE,
  APPLICATION_WITHDRAW_NOT_POSSIBLE_ERROR_CODE
} from '../constants';
import { useApplication, useWithdrawApplication } from '../hooks';
import {
  TalentApplication,
  TalentApplicationStatus,
  TalentApplicationStepState
} from '../types';
import { interviewDisplay } from '../utils';
import { ApplicationCard, ApplicationStateBadges } from './ApplicationCard';
import {
  ApplicationNotice,
  AppliedWith,
  FirstCallNotice,
  NeedsYouNotice,
  SharedReasonNotice
} from './ApplicationExtras';
import { ApplicationSkeleton } from './ApplicationSkeleton';
import { StepList } from './StepList';
import { WithdrawDialog } from './WithdrawDialog';

/** The refusals the talent can do something about; everything else shares one honest message. */
const WITHDRAW_ERROR_COPY: Record<string, TranslationKey> = {
  [APPLICATION_WITHDRAW_NOT_POSSIBLE_ERROR_CODE]:
    'applications.withdraw.notPossible',
  [APPLICATION_NOT_FOUND_ERROR_CODE]: 'applications.notFound.body1'
};

/**
 * Where the withdrawal would be, once it is not offered: what happened, in the talent's terms. A withdrawn application
 * was the talent's own doing, not something that "can no longer" be done; ACTIVE is a late step the backend locks.
 */
const WITHDRAW_CLOSED_COPY: Record<TalentApplicationStatus, TranslationKey> = {
  [TalentApplicationStatus.ACTIVE]: 'applications.detail.withdraw.locked',
  [TalentApplicationStatus.HIRED]: 'applications.detail.withdraw.hired',
  [TalentApplicationStatus.REJECTED]: 'applications.detail.withdraw.closed',
  [TalentApplicationStatus.WITHDRAWN]: 'applications.detail.withdraw.withdrawn'
};

const PROCESS_HEADING_ID = 'application-process-title';

const currentStepKeyOf = (application: TalentApplication): string =>
  application.steps.find(
    step => step.state === TalentApplicationStepState.CURRENT
  )?.key ?? '';

/**
 * @param refId the application the route names; it is the talent's own or the backend says not-found
 * @param variant the PRO_ATS arm the talent is in, reported with the withdrawal for the A/B readout
 */
export const ApplicationDetail = ({
  refId,
  variant,
  renderChangeWeek,
  renderJobHeader,
  jobDetails
}: {
  refId: string;
  variant: string;
  /**
   * PJL: the way to change the week sent with the application. The week editor belongs to the apply flow (jobListings),
   * so the page that composes both hands it in; without it, the week is shown and not offered for change.
   */
  renderChangeWeek?: (application: TalentApplication) => ReactNode;
  /**
   * PJL: the job page of the application. With a header, the page is the job's, drawn as the job page draws itself:
   * its header (with where the application stands in its badge row, from `badges`), "Deine Bewerbung" with the steps
   * and notices, "Dein Interview", "Damit hast du dich beworben", then `jobDetails`, and the withdrawal in the footer.
   * Without one, the application reads as before and `jobDetails` (the job part's skeleton while the listing loads)
   * follows it. The page that composes both modules hands them in; the listing is read there.
   */
  renderJobHeader?: (badges: ReactNode) => ReactNode;
  jobDetails?: ReactNode;
}) => {
  const { application, isLoading, isError, isUnavailable, isNotFound } =
    useApplication(refId);
  const { withdraw, isWithdrawing, errorCode, reset } =
    useWithdrawApplication(refId);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (isLoading) return <ApplicationSkeleton />;

  if (isNotFound)
    return (
      <NotFoundErrorScreen
        title={getTrans('applications.notFound.title')}
        description1={getTrans('applications.notFound.body1')}
        description2={getTrans('applications.notFound.body2')}
      />
    );

  if (isUnavailable)
    return (
      <NotFoundErrorScreen
        title={getTrans('applications.unavailable.title')}
        description1={getTrans('applications.unavailable.body1')}
        description2={getTrans('applications.unavailable.body2')}
      />
    );

  if (isError)
    return (
      <NotFoundErrorScreen
        title={getTrans('applications.error.title')}
        description1={getTrans('applications.error.body1')}
        description2={getTrans('applications.error.body2')}
      />
    );

  if (!application) return null;

  const openDialog = () => {
    // Whatever the last attempt ran into is history; the talent is asking again.
    reset();
    setIsDialogOpen(true);
  };

  const withdrawMessage = errorCode
    ? getTrans(WITHDRAW_ERROR_COPY[errorCode] ?? 'applications.withdraw.error')
    : undefined;

  const handleConfirm = (reasonText: string) => {
    const stepKey = currentStepKeyOf(application);
    withdraw(reasonText, {
      onSuccess: () => {
        sendAtsApplicationWithdrawn({
          variant,
          stepKey,
          reasonLength: reasonText.length
        });
        setIsDialogOpen(false);
      }
    });
  };

  const withdrawDialog = (
    <WithdrawDialog
      isOpen={isDialogOpen}
      isWithdrawing={isWithdrawing}
      errorMessage={withdrawMessage}
      onClose={() => setIsDialogOpen(false)}
      onConfirm={handleConfirm}
    />
  );

  if (renderJobHeader) {
    return (
      <Box data-testid="application-job-page">
        {renderJobHeader(<ApplicationStateBadges application={application} />)}
        <JobPageSection
          title={getTrans('applicationDetailPage.title')}
          testId="application-process"
          headingId={PROCESS_HEADING_ID}
        >
          <NeedsYouNotice application={application} />
          <ApplicationNotice application={application} jobPage />
          <SharedReasonNotice application={application} />
          <StepList steps={application.steps} />
          <FirstCallNotice application={application} jobPage />
        </JobPageSection>
        {application.interview && interviewDisplay(application) === 'card' && (
          <TalentInterviewCard
            refId={application.interview.refId}
            layout="section"
          />
        )}
        {interviewDisplay(application) === 'history' && (
          <JobPageSection
            title={getTrans('interviews.card.title')}
            headingId="application-interview-history-title"
          >
            <Text
              variant="body2"
              color="text.secondary"
              component="p"
              data-testid="application-interview-history"
            >
              {getTrans(
                application.interview?.status === InterviewStatus.EXPIRED
                  ? 'applications.detail.interviewExpired'
                  : 'applications.detail.interviewCancelled'
              )}
            </Text>
          </JobPageSection>
        )}
        <AppliedWith
          application={application}
          changeWeek={renderChangeWeek?.(application)}
          jobPage
        />
        {jobDetails}
        {/* The footer is fixed above the navigation; the last section keeps clear of it. */}
        <Box data-testid="application-withdraw-seam" sx={{ height: 128 }} />
        <JobPageFooter testId="application-withdraw-footer">
          {application.canWithdraw ? (
            <AppButton
              fullWidth
              variant="outlined"
              testId="withdraw-application"
              onClick={openDialog}
            >
              {getTrans('applications.detail.withdraw.action')}
            </AppButton>
          ) : (
            <Text
              variant="body2"
              color="text.secondary"
              component="p"
              data-testid="application-withdraw-closed"
              sx={{ textAlign: 'center' }}
            >
              {getTrans(WITHDRAW_CLOSED_COPY[application.status])}
            </Text>
          )}
        </JobPageFooter>
        {withdrawDialog}
      </Box>
    );
  }

  const process = (
    <>
      <ApplicationNotice application={application} />
      <ApplicationCard application={application} tracker="full" />
      <FirstCallNotice application={application} />
      {application.interview && interviewDisplay(application) === 'card' && (
        <Box sx={{ px: 2.5, pb: 2 }}>
          <TalentInterviewCard refId={application.interview.refId} />
        </Box>
      )}
      {interviewDisplay(application) === 'history' && (
        <Box sx={{ px: 2.5, pb: 2 }}>
          <Text
            variant="body2"
            color="text.secondary"
            data-testid="application-interview-history"
          >
            {getTrans(
              application.interview?.status === InterviewStatus.EXPIRED
                ? 'applications.detail.interviewExpired'
                : 'applications.detail.interviewCancelled'
            )}
          </Text>
        </Box>
      )}
      <AppliedWith
        application={application}
        changeWeek={renderChangeWeek?.(application)}
      />
      <Box sx={{ px: 2.5, pb: 4, textAlign: 'center' }}>
        {application.canWithdraw ? (
          <AppButton
            fullWidth
            variant="outlined"
            color="error"
            testId="withdraw-application"
            onClick={openDialog}
          >
            {getTrans('applications.detail.withdraw.action')}
          </AppButton>
        ) : (
          <Text
            variant="caption"
            component="p"
            data-testid="application-withdraw-closed"
          >
            {getTrans(WITHDRAW_CLOSED_COPY[application.status])}
          </Text>
        )}
      </Box>
      {withdrawDialog}
    </>
  );

  const today = (
    <Box
      component="section"
      aria-label={getTrans('applicationDetailPage.title')}
    >
      {process}
    </Box>
  );

  if (jobDetails === undefined || jobDetails === null) return today;

  return (
    <>
      {today}
      {jobDetails}
    </>
  );
};
